import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton,
  CircularProgress, Collapse, Badge, Button, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Avatar, Stack, Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as ApproveIcon,
  Cancel as DisapproveIcon,
  Visibility as ViewIcon,
  NotificationsActive as BellIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getAllPendingReversalsdb, approveReversaldb, disapproveReversaldb } from '../BaseURL';
import { formatWithOutSom, formatDateSafe } from '../Message';

const tableTypeColor = (type) => {
  const map = {
    medication:     { bg: '#e8f5e9', color: '#2e7d32' },
    service_charge: { bg: '#e3f2fd', color: '#1565c0' },
    lab_charge:     { bg: '#f3e5f5', color: '#7b1fa2' },
    other_charge:   { bg: '#fff3e0', color: '#e65100' },
  };
  return map[(type || '').toLowerCase()] || { bg: '#f5f5f5', color: '#616161' };
};

const modeColor = (mode) => {
  const map = {
    paid:   { bg: '#e8f5e9', color: '#2e7d32' },
    credit: { bg: '#fff8e1', color: '#f57f17' },
  };
  return map[(mode || '').toLowerCase()] || { bg: '#f5f5f5', color: '#616161' };
};

const ReversalNotificationPanel = ({ currentUser }) => {
  const [expanded, setExpanded]           = useState(false);
  const [reversals, setReversals]         = useState([]);
  const [loading, setLoading]             = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [viewDialog, setViewDialog]       = useState({ open: false, row: null });
  const [disapproveDialog, setDisapproveDialog] = useState({ open: false, row: null });
  const [disapproveReason, setDisapproveReason] = useState('');

  const loadReversals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getAllPendingReversalsdb);
      if (res.data.success) setReversals(res.data.results);
    } catch (err) {
      console.error('loadReversals error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReversals();
    const interval = setInterval(loadReversals, 60000);
    return () => clearInterval(interval);
  }, [loadReversals]);

  const handleApprove = async (row) => {
    const confirm = await Swal.fire({
      title: 'Approve Reversal?',
      html: `Approve reversal for <b>${row.charge}</b><br/>
             Patient: <b>${row.patient_name || row.Patient_id}</b><br/>
             Amount: <b>${formatWithOutSom(row.amount)}</b>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2e7d32',
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    setActionLoading(row.id);
    try {
      const res = await axios.post(approveReversaldb, { reversalId: row.id, approvedBy: currentUser });
      if (res.data.success) {
        await Swal.fire({ title: 'Approved!', icon: 'success', timer: 1800, showConfirmButton: false });
        loadReversals();
      } else throw new Error(res.data.message);
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisapprove = async () => {
    const { row } = disapproveDialog;
    setDisapproveDialog({ open: false, row: null });
    setActionLoading(row.id);
    try {
      const res = await axios.post(disapproveReversaldb, {
        reversalId: row.id, disapprovedBy: currentUser, reason: disapproveReason,
      });
      if (res.data.success) {
        await Swal.fire({ title: 'Disapproved', icon: 'info', timer: 1800, showConfirmButton: false });
        loadReversals();
      } else throw new Error(res.data.message);
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setActionLoading(null);
      setDisapproveReason('');
    }
  };

  const count = reversals.length;

  return (
    <>
      <Paper elevation={3} sx={{
        mb: 3, borderRadius: 3, overflow: 'hidden',
        border: count > 0 ? '2px solid #e65100' : '1px solid #e0e0e0',
        transition: 'all 0.3s'
      }}>
        {/* ── Header ── */}
        <Box
          onClick={() => setExpanded(v => !v)}
          sx={{
            px: 3, py: 2,
            background: count > 0
              ? 'linear-gradient(135deg,#bf360c 0%,#e65100 50%,#ff9800 100%)'
              : 'linear-gradient(135deg,#37474f 0%,#546e7a 100%)',
            color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', userSelect: 'none',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Badge
              badgeContent={count}
              color="error"
              max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', fontWeight: 700 } }}
            >
              <Box sx={{
                width: 44, height: 44, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <BellIcon sx={{ fontSize: 26 }} />
              </Box>
            </Badge>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                Reversal Requests
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {count > 0
                  ? `${count} pending request${count !== 1 ? 's' : ''} awaiting admin approval`
                  : 'No pending reversal requests'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton
                size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                onClick={(e) => { e.stopPropagation(); loadReversals(); }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1, px: 0.5 }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          </Stack>
        </Box>

        {/* ── Table ── */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {count === 0 ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <ApproveIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={600}>
                ✅ All caught up — no pending reversal requests.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 460 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['#', 'Date', 'Patient', 'Charge / Description', 'Amount', 'Mode', 'Type', 'Requested By', 'Actions'].map(h => (
                      <TableCell key={h} sx={{
                        bgcolor: '#fff3e0',
                        fontWeight: 700,
                        color: '#bf360c',
                        fontSize: '0.72rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #ffcc80',
                        py: 1.5,
                        whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reversals.map((rev, idx) => {
                    const ttc = tableTypeColor(rev.table_type);
                    const mc  = modeColor(rev.mode);
                    const isActioning = actionLoading === rev.id;

                    return (
                      <TableRow
                        key={rev.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: '#fffde7' },
                          '&:hover': { bgcolor: '#fff8e1' },
                          transition: 'background 0.15s',
                          opacity: isActioning ? 0.6 : 1,
                        }}
                      >
                        {/* # */}
                        <TableCell sx={{ fontSize: '0.75rem', color: '#9e9e9e', fontWeight: 600 }}>
                          {idx + 1}
                        </TableCell>

                        {/* Date */}
                        <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap', color: '#555' }}>
                          {formatDateSafe(rev.date)}
                        </TableCell>

                        {/* Patient */}
                        <TableCell sx={{ minWidth: 140 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 26, height: 26, bgcolor: '#e3f2fd', fontSize: '0.65rem' }}>
                              <PersonIcon sx={{ fontSize: 14, color: '#1565c0' }} />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                                {rev.patient_name || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                {rev.Patient_id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Charge / Description */}
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" fontWeight={700} noWrap title={rev.charge} sx={{ fontSize: '0.8rem' }}>
                            {rev.charge}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap title={rev.details} sx={{ fontSize: '0.7rem' }}>
                            {rev.details}
                          </Typography>
                        </TableCell>

                        {/* Amount */}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography fontWeight={800} sx={{ color: '#c62828', fontSize: '0.88rem' }}>
                            {formatWithOutSom(rev.amount)}
                          </Typography>
                        </TableCell>

                        {/* Mode */}
                        <TableCell>
                          <Chip
                            label={(rev.mode || 'N/A').toUpperCase()}
                            size="small"
                            sx={{
                              fontSize: '0.65rem', height: 22, fontWeight: 700,
                              bgcolor: mc.bg, color: mc.color,
                              border: `1px solid ${mc.color}30`
                            }}
                          />
                        </TableCell>

                        {/* Type */}
                        <TableCell>
                          <Chip
                            label={rev.table_type || '—'}
                            size="small"
                            sx={{
                              fontSize: '0.65rem', height: 22, fontWeight: 700,
                              bgcolor: ttc.bg, color: ttc.color,
                              border: `1px solid ${ttc.color}30`
                            }}
                          />
                        </TableCell>

                        {/* Requested By */}
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#555' }}>
                          {rev.requested_by}
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="View details">
                              <IconButton size="small" disabled={isActioning}
                                onClick={() => setViewDialog({ open: true, row: rev })}
                                sx={{ color: '#1976d2', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, width: 28, height: 28 }}>
                                <ViewIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Approve">
                              <IconButton size="small" disabled={isActioning}
                                onClick={() => handleApprove(rev)}
                                sx={{ color: '#2e7d32', bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' }, width: 28, height: 28 }}>
                                {isActioning ? <CircularProgress size={14} /> : <ApproveIcon sx={{ fontSize: 15 }} />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Disapprove">
                              <IconButton size="small" disabled={isActioning}
                                onClick={() => { setDisapproveDialog({ open: true, row: rev }); setDisapproveReason(''); }}
                                sx={{ color: '#c62828', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' }, width: 28, height: 28 }}>
                                <DisapproveIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Footer summary */}
          {count > 0 && (
            <Box sx={{
              px: 3, py: 1.5,
              bgcolor: '#fff3e0',
              borderTop: '1px solid #ffcc80',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <Typography variant="caption" color="#e65100" fontWeight={700}>
                {count} pending reversal{count !== 1 ? 's' : ''} — total:&nbsp;
                <strong>
                  {formatWithOutSom(reversals.reduce((s, r) => s + Number(r.amount || 0), 0))}
                </strong>
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Auto-refreshes every 60 s
              </Typography>
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* ── View Detail Dialog ── */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, row: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: 'linear-gradient(135deg,#bf360c,#ff9800)', color: 'white', px: 3, py: 2 }}>
            <Typography variant="h6" fontWeight={700}>Reversal Request Detail</Typography>
          </Box>
        </DialogTitle>
        {viewDialog.row && (
          <DialogContent sx={{ pt: 3 }}>
            {[
              { label: 'Patient Name',  value: viewDialog.row.patient_name || '—' },
              { label: 'Patient ID',    value: viewDialog.row.Patient_id },
              { label: 'Charge',        value: viewDialog.row.charge },
              { label: 'Description',   value: viewDialog.row.details },
              { label: 'Amount',        value: formatWithOutSom(viewDialog.row.amount) },
              { label: 'Mode',          value: viewDialog.row.mode || '—' },
              { label: 'Type',          value: viewDialog.row.table_type },
              { label: 'Receipt',       value: viewDialog.row.receipt || '—' },
              { label: 'Requested By',  value: viewDialog.row.requested_by },
              { label: 'Request Date',  value: formatDateSafe(viewDialog.row.date) },
              { label: 'Status',        value: viewDialog.row.status || 'pending' },
            ].map(({ label, value }) => (
              <Box key={label} sx={{
                display: 'flex', justifyContent: 'space-between',
                py: 1, borderBottom: '1px solid #f0f0f0'
              }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
                <Typography variant="body2" fontWeight={700}>{value}</Typography>
              </Box>
            ))}
            <Alert severity="warning" sx={{ mt: 2, fontSize: '0.75rem' }}>
              Approving this will insert a reversal entry into the income ledger.
            </Alert>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setViewDialog({ open: false, row: null })} variant="outlined">Close</Button>
          {viewDialog.row && (
            <>
              <Button variant="contained" color="success" startIcon={<ApproveIcon />}
                onClick={() => { setViewDialog({ open: false, row: null }); handleApprove(viewDialog.row); }}>
                Approve
              </Button>
              <Button variant="contained" color="error" startIcon={<DisapproveIcon />}
                onClick={() => {
                  setDisapproveDialog({ open: true, row: viewDialog.row });
                  setViewDialog({ open: false, row: null });
                }}>
                Disapprove
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Disapprove Dialog ── */}
      <Dialog open={disapproveDialog.open} onClose={() => setDisapproveDialog({ open: false, row: null })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: 'linear-gradient(135deg,#b71c1c,#e53935)', color: 'white', px: 3, py: 2 }}>
            <Typography variant="h6" fontWeight={700}>Disapprove Reversal</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {disapproveDialog.row && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1.5, border: '1px solid #ffcc80' }}>
              <Typography variant="body2" fontWeight={700} color="#bf360c">
                {disapproveDialog.row.charge}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Patient: {disapproveDialog.row.patient_name || disapproveDialog.row.Patient_id}
                &nbsp;•&nbsp;{formatWithOutSom(disapproveDialog.row.amount)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth multiline rows={3}
            label="Reason (optional)"
            value={disapproveReason}
            onChange={(e) => setDisapproveReason(e.target.value)}
            placeholder="State reason for disapproval..."
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDisapproveDialog({ open: false, row: null })} variant="outlined">Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DisapproveIcon />} onClick={handleDisapprove}>
            Disapprove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReversalNotificationPanel;